import React, { useState, useEffect, useMemo } from 'react';
import {
    IconButton,
    Link as MuiLink,
    MenuItem,
    Pagination as MuiPagination,
    Select as MuiSelect,
    Stack,
    Typography,
    Box,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import Check from '@mds/icons/Check';
import CheckCircle from '@mds/icons/CheckCircle';
import XCircle from '@mds/icons/XCircle';
import { StyledAgGridContainer, useAgGridTheme } from '@mds/ag-grid';
import { EmptyState } from '@mds/ui';
import ListPageHeader from '../ListPageHeader';
import NotificationMessage from '../NotificationMessage';
import {
    ColDef,
    GridOptions,
    GridApi,
    ModuleRegistry,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ApolloError } from 'apollo-client';
import { Link as RouterLink, Router, useHistory } from 'react-router-dom';
import { History } from 'history';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { baseUrls } from '../../../utils/baseUrls.utils';
import {
    FilterContextData,
    FilterType,
} from '../../providers/admin/ArticleFilterProvider';

const agGridLicense: string | undefined = process.env.REACT_APP_AGGRID_LICENSE;

ModuleRegistry.registerModules([AllEnterpriseModule]);

if (agGridLicense) {
    LicenseManager.setLicenseKey(agGridLicense);
}

interface StyleProps {
    showHeader: boolean;
    maxWidth: number | string;
    removeBottomOffset?: boolean;
}

const containerSx = ({ showHeader, removeBottomOffset }: StyleProps) => ({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    scrollbarGutter: 'stable',
    px: showHeader ? 2 : 0,
    mb: removeBottomOffset ? 0 : 6,
});

const mainSx = ({ maxWidth }: StyleProps) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    maxWidth,
});

const listSx = {
    mt: 2,
    overflow: 'hidden',
    minHeight: 244,
    flexGrow: 1,
    '& .ag-cell': {
        display: 'flex',
        alignItems: 'center',
    },
    '& .ag-group-expanded, & .ag-group-contracted': {
        width: 'auto',
        mr: 1.3,
        '& .ag-icon': {
            fontWeight: 'bold',
            fontSize: '18px',
        },
    },
    '& .ag-cell.channel-group-cell': {
        backgroundColor: 'transparent !important',
        '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiSelect-select': {
            backgroundColor: 'transparent !important',
        },
    },
};

const noRecordsSx = {
    mt: 2,
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    height: 244,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
};

const noWrapLinkSx = {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const wrapLinkSx = {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
};

type PageRange = {
    label: string;
    value: number;
};

const ranges: PageRange[] = [
    { label: '15', value: 15 },
    { label: '30', value: 30 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
    { label: '200', value: 200 },
];

export interface AdminListProps<T> {
    items: T[];
    name?: string;
    title?: string;
    columns?: ColDef[];
    autoGroupColumn?: ColDef;
    showAdd: boolean;
    aboveTable?: React.ReactNode;
    toolbar?: React.ReactNode;
    footer?: React.ReactNode;
    loading: boolean;
    error?: ApolloError;
    cellRenderers?: any;
    showHeader?: boolean;
    treeData?: boolean;
    addButtonLabel?: string;
    secondaryButtonLabel?: string;
    entityForCaptionText?: string;
    secondaryButtonUrl?: string;
    itemsNumber?: number;
    listPageHeader?: boolean;
    gridOptions?: GridOptions;
    defaultColDef?: ColDef;
    apiCallback?: (api: GridApi) => void;
    getRowHeight?: (p: any) => number | undefined;
    gridDensity?: 'regular' | 'compact' | 'spacious';
    maxWidth?: number | string;
    showCaption?: boolean;
    overrideContainerClass?: string;
    overrideListClass?: string;
    overrideContainerSx?: SxProps<Theme>;
    overrideListSx?: SxProps<Theme>;
    pagination?: boolean;
    overwritePagination?: boolean;
    overwriteCurrentPage?: number;
    overwritePageRange?: number;
    onCurrentPageChange?: (page: number) => void;
    onPerPageChange?: (page: number) => void;
    showTableWhenEmpty?: boolean;
    removeBottomOffset?: boolean;
    renderGridWhenEmpty?: boolean;
}

const AdminList = <T,>({
    items,
    name,
    title,
    columns,
    showAdd,
    aboveTable,
    toolbar,
    footer,
    loading,
    cellRenderers,
    showHeader = true,
    treeData = false,
    addButtonLabel = '',
    secondaryButtonLabel = '',
    entityForCaptionText = '',
    secondaryButtonUrl = '',
    autoGroupColumn = {},
    itemsNumber = undefined,
    listPageHeader = true,
    gridOptions: customGridOptions = {},
    defaultColDef: customDefaultColDef = {},
    apiCallback = () => {},
    getRowHeight,
    gridDensity = 'regular',
    maxWidth = 1200,
    showCaption = true,
    overrideContainerClass,
    overrideListClass,
    overrideContainerSx,
    overrideListSx,
    pagination = true,
    showTableWhenEmpty = true,
    renderGridWhenEmpty = false,
    overwritePagination = false,
    overwriteCurrentPage,
    overwritePageRange,
    onCurrentPageChange = () => {},
    onPerPageChange = () => {},
    removeBottomOffset,
}: AdminListProps<T>): JSX.Element => {
    const styleProps = {
        showHeader: !!showHeader,
        maxWidth,
        removeBottomOffset,
    };
    const history = useHistory();
    const agGridTheme = useAgGridTheme({ density: gridDensity });
    const defaultRange = overwritePageRange
        ? ranges.find((r) => r.value === overwritePageRange) || ranges[0]
        : !treeData
          ? ranges[0]
          : ranges[3];
    const [selectedRange, setSelectedRange] = useState(defaultRange);
    const [currentPage, setCurrentPage] = useState(overwriteCurrentPage || 1);
    const [grid, setGrid] = useState<GridApi | undefined>();
    const columnDefs = useMemo(
        () => withoutLastColumnResize(columns),
        [columns]
    );

    const rowsPerPage = selectedRange.value;

    const grOptions: GridOptions = {
        suppressContextMenu: true,
        suppressAutoSize: false,
        suppressHorizontalScroll: false,
        suppressNoRowsOverlay: true,
        suppressScrollOnNewData: true,
        pagination: true,
        paginationPageSize: rowsPerPage,
        paginationPageSizeSelector: false,
        suppressPaginationPanel: true,
        accentedSort: true,
        getRowId: (params) => params.data?._id,
        onGridReady: (event: any) => {
            setGrid(event.api);
            apiCallback(event.api);
            event.api.sizeColumnsToFit();
            event.api.resetRowHeights();
        },
        onSortChanged: (event: any) => {
            setCurrentPage(1);
        },
        treeData,
        autoGroupColumnDef: autoGroupColumn,
        groupDefaultExpanded: -1,
        getDataPath: (data) => data.categoriesHierarchy,
        components: cellRenderers,
        defaultColDef: {
            autoHeight: false,
            cellDataType: false,
            suppressMovable: true,
            sortable: true,
            ...customDefaultColDef,
        },
        ...customGridOptions,
    };

    useEffect(
        function gotoFirstPageWhenFiltersChange() {
            if (overwritePagination) return;
            setCurrentPage(1);
        },
        [items]
    );

    useEffect(() => {
        if (overwritePagination) return;
        grid?.paginationGoToPage(currentPage - 1);
    }, [grid, currentPage]);

    useEffect(() => {
        if (overwritePagination) return;
        setCurrentPage(1);
        grid?.setGridOption('paginationPageSize', rowsPerPage);
    }, [grid, rowsPerPage]);

    useEffect(() => {
        if (overwritePagination && grid && rowsPerPage) {
            grid.setGridOption('paginationPageSize', rowsPerPage);
        }
    }, [grid, rowsPerPage, overwritePagination]);

    useEffect(() => {
        if (overwritePageRange) {
            const nextRange =
                ranges.find((r) => r.value === overwritePageRange) || ranges[0];
            setSelectedRange(nextRange);
        }
    }, [overwritePageRange]);

    useEffect(() => {
        onCurrentPageChange(currentPage);
    }, [currentPage]);

    useEffect(() => {
        overwriteCurrentPage && setCurrentPage(overwriteCurrentPage);
    }, [overwriteCurrentPage]);

    useEffect(() => {
        onPerPageChange(rowsPerPage);
    }, [rowsPerPage]);

    const itemsLength = itemsNumber ? itemsNumber : items.length;
    const pageCaption = showCaption
        ? getCaption(
              currentPage,
              itemsLength,
              itemsLength,
              rowsPerPage,
              !treeData
          )
        : undefined;
    const addNewUrl =
        showAdd && name
            ? `${baseUrls.publisher.main}/${name.toLowerCase()}/add`
            : '';
    const pageHeaderTitle = title ? title : name;

    if (itemsLength === 0 && !loading && !renderGridWhenEmpty) {
        return (
            <Box
                className={overrideContainerClass}
                sx={[
                    containerSx(styleProps),
                    ...(Array.isArray(overrideContainerSx)
                        ? overrideContainerSx
                        : overrideContainerSx
                          ? [overrideContainerSx]
                          : []),
                ]}
            >
                <Box sx={mainSx(styleProps)}>
                    <Box>
                        <ListPageHeader
                            title={pageHeaderTitle}
                            entityForCaptionText={entityForCaptionText}
                            addButtonLabel={addButtonLabel}
                            secondaryButtonLabel={secondaryButtonLabel}
                            showAdd={showAdd}
                            showSecondaryButton={!!secondaryButtonLabel}
                            secondaryButtonUrl={secondaryButtonUrl}
                            addNewUrl={addNewUrl}
                            toolbar={toolbar}
                            pageCaption={pageCaption}
                            showHeader={showHeader}
                        />
                    </Box>
                    {aboveTable}
                    {showTableWhenEmpty && (
                        <Box sx={noRecordsSx}>
                            <EmptyState title="There are no items to show" />
                        </Box>
                    )}
                </Box>
                <div>
                    <NotificationMessage />
                </div>
            </Box>
        );
    }

    return (
        <Box
            className={overrideContainerClass}
            sx={[
                containerSx(styleProps),
                ...(Array.isArray(overrideContainerSx)
                    ? overrideContainerSx
                    : overrideContainerSx
                      ? [overrideContainerSx]
                      : []),
            ]}
        >
            <Box sx={mainSx(styleProps)}>
                {listPageHeader && (
                    <Box>
                        <ListPageHeader
                            title={pageHeaderTitle}
                            entityForCaptionText={entityForCaptionText}
                            addButtonLabel={addButtonLabel}
                            secondaryButtonLabel={secondaryButtonLabel}
                            showAdd={showAdd}
                            showSecondaryButton={!!secondaryButtonLabel}
                            secondaryButtonUrl={secondaryButtonUrl}
                            addNewUrl={addNewUrl}
                            toolbar={toolbar}
                            pageCaption={pageCaption}
                            showHeader={showHeader}
                        />
                    </Box>
                )}

                <div>
                    <NotificationMessage />
                </div>
                {aboveTable}
                <Box
                    className={overrideListClass}
                    sx={[
                        listSx,
                        ...(Array.isArray(overrideListSx)
                            ? overrideListSx
                            : overrideListSx
                              ? [overrideListSx]
                              : []),
                    ]}
                >
                    <StyledAgGridContainer style={{ width: '100%' }}>
                        <AgGridReact
                            rowData={items}
                            domLayout="autoHeight"
                            columnDefs={columnDefs}
                            context={{ history }}
                            onRowDataUpdated={onRowDataChanged}
                            gridOptions={grOptions}
                            getRowHeight={getRowHeight}
                            theme={agGridTheme.theme}
                            chartThemes={agGridTheme.chartThemes}
                        />
                    </StyledAgGridContainer>
                </Box>

                {pagination && !loading && !treeData && (
                    <Box sx={{ mt: 2 }}>
                        <AdminPagination
                            id={'admin-list-pagination'}
                            ranges={ranges}
                            onRangeChanged={setSelectedRange}
                            selectedRange={selectedRange}
                            currentPage={currentPage}
                            maxPage={Math.ceil(itemsLength / rowsPerPage)}
                            totalRecords={itemsLength}
                            recordsPerPage={rowsPerPage}
                            onPageChanged={(delta: number): void =>
                                setCurrentPage(currentPage + delta)
                            }
                        />
                    </Box>
                )}
                {footer}
            </Box>
        </Box>
    );
};

interface AdminPaginationProps {
    id: string;
    ranges: PageRange[];
    selectedRange: PageRange;
    currentPage: number;
    maxPage: number;
    totalRecords: number;
    recordsPerPage: number;
    onRangeChanged: (range: PageRange) => void;
    onPageChanged: (delta: number) => void;
}

const AdminPagination = ({
    id,
    ranges,
    selectedRange,
    currentPage,
    maxPage,
    totalRecords,
    onRangeChanged,
    onPageChanged,
}: AdminPaginationProps): JSX.Element => {
    const safeMaxPage = Math.max(maxPage, 1);

    return (
        <Stack
            id={id}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    color: 'text.secondary',
                }}
            >
                {totalRecords} records
            </Typography>
            <Stack
                direction="row"
                spacing={2}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        Rows
                    </Typography>
                    <MuiSelect
                        size="small"
                        hiddenLabel
                        inputProps={{ 'aria-label': 'Rows per page' }}
                        value={selectedRange.value}
                        onChange={(event) => {
                            const nextRange = ranges.find(
                                (range) =>
                                    range.value === Number(event.target.value)
                            );
                            if (nextRange) onRangeChanged(nextRange);
                        }}
                    >
                        {ranges.map((range) => (
                            <MenuItem key={range.value} value={range.value}>
                                {range.label}
                            </MenuItem>
                        ))}
                    </MuiSelect>
                </Stack>
                <MuiPagination
                    count={safeMaxPage}
                    page={Math.min(currentPage, safeMaxPage)}
                    onChange={(_, nextPage) =>
                        onPageChanged(nextPage - currentPage)
                    }
                    size="small"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                />
            </Stack>
        </Stack>
    );
};

export default AdminList;

function getCaption(
    currentPage: number,
    selectedRecords: number,
    totalRecords: number,
    perPage: number,
    showRange = true
): string {
    if (selectedRecords === 0) {
        return '0';
    }

    if (!showRange) {
        return `${totalRecords}`;
    }

    const from = (currentPage - 1) * perPage + 1;
    const to = Math.min(from + perPage - 1, totalRecords);

    return `${from}-${to} of ${totalRecords}`;
}

function withoutLastColumnResize(columns?: ColDef[]): ColDef[] | undefined {
    if (!columns?.length) return columns;

    const lastColumnIndex = columns.length - 1;

    return columns.map((column, index) =>
        index === lastColumnIndex ? { ...column, resizable: false } : column
    );
}

export const TemplateNameCellRenderer = (props: any): JSX.Element => {
    const path = !props.addFromTemplate
        ? `${baseUrls.publisher.main}/${props.apiName}/edit/${window.encodeURIComponent(props.data._id)}`
        : `${baseUrls.publisher.articles.addFromTemplate}/${window.encodeURIComponent(props.data._id)}`;

    if (!props.context?.history)
        throw new Error(
            'React router history must be added to ag-grid context'
        );

    return (
        <Router history={props.context.history}>
            {props.data.publishedBy ? (
                <LinkCellRenderer
                    url={path}
                    label={props.value}
                    history={props.context?.history}
                    {...props}
                />
            ) : (
                props.value
            )}
        </Router>
    );
};

export const NameCellRenderer = (props: any): JSX.Element => {
    const toPath = `${baseUrls.publisher.main}/${props.apiName.toLowerCase()}/edit/${window.encodeURIComponent(props.data._id)}`;

    if (!props.context?.history)
        throw new Error(
            'React router history must be added to ag-grid context'
        );

    const label = props.data.parent ? '— ' + props.value : props.value;

    return (
        <LinkCellRenderer
            url={toPath}
            label={label}
            history={props.context?.history}
            {...props}
        />
    );
};

export const ReadOnlyLabelCellRenderer = (props: any): JSX.Element => {
    return !props.data.parent ? props.value : '— ' + props.value;
};

export const CheckIconCellRendered = (props: any): JSX.Element => {
    if (!props.value) {
        return <></>;
    }

    return <Check fontSize="small" />;
};

export interface LinkCellProps {
    url: string;
    label: React.ReactNode;
    history: any;
}

export const LinkCellRenderer = (props: any): JSX.Element => {
    const { history, label, wrap = false, boldLabel = true } = props;

    if (!history)
        throw new Error(
            'React router history must be added to ag-grid context'
        );

    return (
        <Box sx={{ width: '100%' }}>
            <Router history={props.history}>
                <MuiLink
                    component={RouterLink}
                    to={props.url}
                    underline="hover"
                    sx={wrap ? wrapLinkSx : noWrapLinkSx}
                >
                    {boldLabel ? <Box component="strong">{label}</Box> : label}
                </MuiLink>
            </Router>
        </Box>
    );
};

interface ArticleCountCellRendererProps {
    filter: FilterContextData;
    value: number;
    context?: any;
    filterData: {
        _id: string;
        name: string;
        type: FilterType;
    };
}

export const ArticleCountCellRenderer = (
    props: ArticleCountCellRendererProps
): JSX.Element => {
    if (props.value === 0) {
        return (
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                }}
            >
                No articles
            </Typography>
        );
    }

    if (!props.context?.history)
        throw new Error(
            'React router history must be added to ag-grid context'
        );

    const articleFilter = props.filter;
    const label = props.value
        ? props.value + ' article' + (props.value > 1 ? 's' : '')
        : 'Loading…';
    const toPath = baseUrls.publisher.articles.list;

    const clickHandler = () => {
        const filter = props.filterData;
        articleFilter.clear();
        articleFilter.add(filter);
        props.context?.history.push(toPath);
    };

    return (
        <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap' }}>
            <Router history={props.context?.history}>
                <MuiLink
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={clickHandler}
                >
                    {label}
                </MuiLink>
            </Router>
        </Box>
    );
};

interface ArticleViewCellRendererProps {
    value: number;
    linkTo?: 'articles';
    data: {
        _id: string;
    };
    context: {
        history: History;
    };
}

interface ReadOnlyArticleViewCellRendererProps {
    value: number;
}

export const ReadOnlyArticleViewCellRenderer = (
    props: ReadOnlyArticleViewCellRendererProps
): JSX.Element => {
    if (props.value === 0) {
        return <Typography variant="caption">No views</Typography>;
    }

    return (
        <Typography variant="body2">
            {props.value + ' view' + (props.value === 1 ? '' : 's')}
        </Typography>
    );
};

export const ArticleViewCellRenderer = (
    props: ArticleViewCellRendererProps
): JSX.Element => {
    if (!props.context?.history)
        throw new Error(
            'React router history must be added to ag-grid context'
        );

    if (props.value === 0) {
        return <Typography variant="caption">No views</Typography>;
    }

    const label = props.value
        ? props.value + ' view' + (props.value === 1 ? '' : 's')
        : 'Loading…';
    const url =
        props.linkTo &&
        `${baseUrls.publisher.main}/${props.linkTo}/stats/${props.data._id}`;

    return (
        <Router history={props.context?.history}>
            {url ? (
                <MuiLink component={RouterLink} to={url} underline="hover">
                    {label}
                </MuiLink>
            ) : (
                <Typography variant="body2">{label}</Typography>
            )}
        </Router>
    );
};

interface DecisionCellRendererProps {
    onYesClick: () => void;
    onNoClick: () => void;
}

export const DecisionCellRenderer = ({
    onYesClick,
    onNoClick,
}: DecisionCellRendererProps): JSX.Element => {
    return (
        <Stack
            direction="row"
            spacing={2}
            sx={{ flex: 'none', order: 4, flexGrow: 0 }}
        >
            <IconButton
                color="success"
                onClick={onYesClick}
                sx={{ order: 0, flexGrow: 0, width: 'auto', minWidth: 1 }}
            >
                <CheckCircle />
            </IconButton>
            <IconButton
                color="error"
                onClick={onNoClick}
                sx={{ order: 1, flexGrow: 0, width: 'auto', minWidth: 1 }}
            >
                <XCircle />
            </IconButton>
        </Stack>
    );
};

function onRowDataChanged(event: any) {
    event.api.resetRowHeights();
}

export const sortColByStringProp =
    <T extends { [P in K]: string }, K extends keyof T>(field: K) =>
    (valueA: T | undefined, valueB: T | undefined) => {
        if (!valueA?.[field]) return -1;
        if (!valueB?.[field]) return 1;
        console.log(
            valueA?.[field],
            valueB?.[field],
            valueA[field].localeCompare(valueB[field] ?? '')
        );
        return valueA[field].localeCompare(valueB[field] ?? '');
    };
